import { Alert } from '@/global/components/Alert'
import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { Input } from '@/global/components/ui/input'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { useCredenciaisApiOutletContext } from 'pages/CredenciaisApi'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiClientWithSecret, apiClients } from 'service/api'
import { toast } from 'sonner'

export default function RegenerarSegredoCredencial() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { data } = useCredenciaisApiOutletContext()
    const credencial = data?.find(item => item.id === Number(id))

    const [loading, setLoading] = useState(false)
    const [resultado, setResultado] = useState<ApiClientWithSecret | null>(null)
    const [copiedSecret, setCopiedSecret] = useState(false)

    if (!credencial) return null

    const handleClose = () => {
        setResultado(null)
        navigate('/integracoes/credenciais-api')
    }

    const handleRegenerar = async () => {
        setLoading(true)
        try {
            const res = (await apiClients.regenerarSegredo(
                credencial.id,
            )) as unknown as ApiClientWithSecret
            setResultado(res)
            await queryClient.invalidateQueries(apiClients.queryKey())
            toast.success('Segredo regenerado com sucesso')
        } catch (err: any) {
            toast.error(err?.message || 'Erro ao regenerar segredo')
        } finally {
            setLoading(false)
        }
    }

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedSecret(true)
            setTimeout(() => setCopiedSecret(false), 2000)
            toast.success('Copiado para a área de transferência')
        } catch {
            toast.error('Erro ao copiar')
        }
    }

    if (resultado) {
        return (
            <Dialog.Root open onClose={handleClose}>
                <Dialog.Title>Novo Segredo Gerado</Dialog.Title>
                <Dialog.Content>
                    <Alert severity="warning" className="mb-4">
                        <strong>Atenção:</strong> O segredo anterior foi
                        revogado e este novo segredo só será exibido esta vez!
                        Copie e guarde-o em um local seguro.
                    </Alert>

                    <div className="flex flex-col gap-4">
                        <div>
                            <span className="text-xs font-semibold text-muted-foreground">
                                Aplicação
                            </span>
                            <p className="text-sm font-medium text-foreground">
                                {resultado.nome} ({resultado.clientId})
                            </p>
                        </div>

                        <Input
                            label="Novo Client Secret"
                            value={resultado.clientSecret}
                            readOnly
                            endAdornment={
                                <IconButton
                                    onClick={() =>
                                        copyToClipboard(resultado.clientSecret)
                                    }
                                    title="Copiar Novo Segredo"
                                >
                                    {copiedSecret ? (
                                        <CheckIcon className="size-4 text-success" />
                                    ) : (
                                        <CopyIcon className="size-4" />
                                    )}
                                </IconButton>
                            }
                        />
                    </div>
                </Dialog.Content>
                <Dialog.Actions>
                    <Button variant="contained" onClick={handleClose}>
                        Concluir
                    </Button>
                </Dialog.Actions>
            </Dialog.Root>
        )
    }

    return (
        <Dialog.Root
            width="md"
            loading={loading}
            open={hasPermission(['update', 'CredenciaisDeAPI'])}
            onClose={handleClose}
        >
            <Dialog.Title>Regenerar Segredo de API</Dialog.Title>
            <Dialog.Content>
                <p className="text-sm text-foreground">
                    Tem certeza que deseja regenerar o segredo da credencial{' '}
                    <strong>{credencial.nome}</strong> ({credencial.clientId})?
                </p>
                <Alert severity="warning" className="mt-3">
                    As integrações que utilizam o segredo atual deixarão de
                    funcionar imediatamente.
                </Alert>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton onClick={handleClose} />
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleRegenerar}
                    disabled={loading}
                >
                    Regenerar Segredo
                </Button>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
